import React from 'react';
import { Card } from '../ui/card';
import { Badge } from '../ui/badge';
import { FileText, Mail, Presentation, CreditCard, Check } from 'lucide-react';

export default function MarketingCollateral() {
  return (
    <div className="space-y-8">
      <div>
        <h1 className="text-4xl md:text-5xl font-display font-normal text-jackstone-blue mb-4">
          Marketing Collateral Guidelines
        </h1>
        <p className="text-base text-body leading-relaxed">
          Consistent application of brand elements across all marketing materials ensures
          professional presentation and strong brand recognition.
        </p>
      </div>

      <div>
        <h2 className="text-2xl font-heading font-semibold text-jackstone-blue mb-4">
          Collateral Types
        </h2>
        <div className="grid md:grid-cols-2 gap-6">
          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-jackstone-blue rounded-lg flex items-center justify-center">
                <CreditCard className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-display font-normal">Business Cards</h3>
            </div>
            <div className="space-y-3 text-sm text-subtle">
              <p><strong>Format:</strong> 3.5" x 2" (89mm x 51mm)</p>
              <p><strong>Layout:</strong> Logo on front, contact details on back</p>
              <p><strong>Colors:</strong> Primary blue (#39678F) with white text</p>
              <p><strong>Paper:</strong> Premium matte or textured finish</p>
              <p><strong>Typography:</strong> Montserrat for contact information</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-navy rounded-lg flex items-center justify-center">
                <FileText className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-display font-normal">Brochures</h3>
            </div>
            <div className="space-y-3 text-sm text-subtle">
              <p><strong>Format:</strong> Tri-fold or multi-page booklet</p>
              <p><strong>Layout:</strong> Grid-based with generous whitespace</p>
              <p><strong>Imagery:</strong> High-quality product photos and diagrams</p>
              <p><strong>Content:</strong> Technical specifications and benefits</p>
              <p><strong>Call-to-action:</strong> Contact information prominent</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-accent-teal rounded-lg flex items-center justify-center">
                <Presentation className="h-6 w-6 text-white" />
              </div>
              <h3 className="text-lg font-display font-normal">Presentations</h3>
            </div>
            <div className="space-y-3 text-sm text-subtle">
              <p><strong>Template:</strong> Branded PowerPoint/Keynote template</p>
              <p><strong>Title Slide:</strong> Jackstone logo and presentation title</p>
              <p><strong>Content Slides:</strong> Consistent header with logo</p>
              <p><strong>Typography:</strong> Bebas Neue for titles, Montserrat for body</p>
              <p><strong>Colors:</strong> Primary palette with accent highlights</p>
            </div>
          </Card>

          <Card className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 bg-light-blue rounded-lg flex items-center justify-center">
                <Mail className="h-6 w-6 text-body" />
              </div>
              <h3 className="text-lg font-display font-normal">Email Templates</h3>
            </div>
            <div className="space-y-3 text-sm text-subtle">
              <p><strong>Header:</strong> Logo with tagline or page title</p>
              <p><strong>Body:</strong> Clean, readable Montserrat font</p>
              <p><strong>Footer:</strong> Contact info, social links, legal text</p>
              <p><strong>Colors:</strong> White background with primary accents</p>
              <p><strong>CTA Buttons:</strong> Primary blue with white text</p>
            </div>
          </Card>
        </div>
      </div>

      <Card className="p-6 bg-surface">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Letterhead and Stationery
        </h3>
        <div className="grid md:grid-cols-2 gap-6">
          <div>
            <h4 className="font-medium text-body mb-3">Letterhead Design</h4>
            <ul className="space-y-2 text-sm text-subtle">
              <li>• Logo in top left corner (1" from edges)</li>
              <li>• Company address in header or footer</li>
              <li>• Subtle horizontal line separator in primary blue</li>
              <li>• Wide margins (1" all sides) for professional appearance</li>
              <li>• Footer with contact information in small type</li>
            </ul>
          </div>
          <div>
            <h4 className="font-medium text-body mb-3">Digital Stationery</h4>
            <ul className="space-y-2 text-sm text-subtle">
              <li>• Branded Word/Google Docs template</li>
              <li>• PDF letterhead for official documents</li>
              <li>• Consistent header and footer across all documents</li>
              <li>• Digital signature block with contact details</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-jackstone-blue mb-4">
          Trade Show and Event Materials
        </h3>
        <div className="space-y-4">
          <div>
            <Badge className="mb-2">Booth Design</Badge>
            <p className="text-sm text-subtle">
              Large-format graphics featuring product photography on primary blue backgrounds.
              Jackstone logo prominently displayed. Clean, professional layout with minimal text.
            </p>
          </div>
          <div>
            <Badge className="mb-2">Banner Stands</Badge>
            <p className="text-sm text-subtle">
              Vertical format (33" x 80") with hero product image, key benefits, and contact info.
              Use brand colors and maintain visual hierarchy.
            </p>
          </div>
          <div>
            <Badge className="mb-2">Promotional Items</Badge>
            <p className="text-sm text-subtle">
              Branded merchandise (pens, USB drives, notebooks) should feature logo in primary
              blue or white on quality materials that reflect our industrial premium positioning.
            </p>
          </div>
        </div>
      </Card>

      <Card className="p-6 bg-light-blue border-jackstone-blue/20">
        <h3 className="text-xl font-heading font-semibold text-body mb-3">
          Print Specifications
        </h3>
        <div className="grid sm:grid-cols-2 gap-4">
          <div>
            <h4 className="font-medium text-body mb-2">Color Mode</h4>
            <p className="text-sm text-subtle mb-1">CMYK for all print materials</p>
            <p className="text-xs text-subtle">RGB for digital/screen display</p>
          </div>
          <div>
            <h4 className="font-medium text-body mb-2">Resolution</h4>
            <p className="text-sm text-subtle mb-1">300 DPI minimum for print</p>
            <p className="text-xs text-subtle">72-150 DPI for digital</p>
          </div>
          <div>
            <h4 className="font-medium text-body mb-2">File Formats</h4>
            <p className="text-sm text-subtle mb-1">PDF (print-ready with bleed)</p>
            <p className="text-xs text-subtle">AI/EPS for vector graphics</p>
          </div>
          <div>
            <h4 className="font-medium text-body mb-2">Bleed</h4>
            <p className="text-sm text-subtle mb-1">0.125" (3mm) on all sides</p>
            <p className="text-xs text-subtle">Keep text 0.25" from trim</p>
          </div>
        </div>
      </Card>

      <Card className="p-6">
        <h3 className="text-xl font-heading font-semibold text-body mb-3">
          Quality Standards
        </h3>
        <div className="space-y-2">
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <p className="text-sm text-subtle">
              Always proof materials before printing - check colors, alignment, and text
            </p>
          </div>
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <p className="text-sm text-subtle">
              Use high-quality paper stock appropriate for the application
            </p>
          </div>
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <p className="text-sm text-subtle">
              Maintain consistent brand colors across all printed materials
            </p>
          </div>
          <div className="flex items-start gap-3">
            <Check className="h-5 w-5 text-success flex-shrink-0 mt-0.5" />
            <p className="text-sm text-subtle">
              Store print-ready files in a central brand asset library
            </p>
          </div>
        </div>
      </Card>
    </div>
  );
}
